import { useEffect, useRef, useState } from 'react';
import { ImageInputs } from './ImageInputs';
import { SpreadPreview } from './SpreadPreview';
import { MainDownloadButton } from './MainDownloadButton';
import {
  SettingsGroupCardPaper,
  SettingsGroupPrint,
  SettingsGroupCalibration,
} from './SettingsHub';
import { ExampleInlineHint } from './examples/ExampleInlineHint';
import type { AppState } from '../hooks/useAppState';

type StudioTab = 'settings' | 'workspace';

interface Props {
  app: AppState;
}

export function Studio({ app }: Props) {
  const insideEnabled = app.insideEnabled;
  const handleToggleInside = app.setInsideEnabled;

  const [cardTypeCollapsed, setCardTypeCollapsed] = useState(false);

  // Mobile-only: which panel is currently snapped into view. Derived from
  // the studio-body scroll position via a passive listener so swipe and
  // tab-click stay in sync. Desktop layout never triggers the listener
  // because the body's scrollWidth equals clientWidth (no horizontal scroll).
  const [activeTab, setActiveTab] = useState<StudioTab>('settings');
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const onScroll = () => {
      if (body.scrollWidth <= body.clientWidth + 1) return;
      const next: StudioTab =
        body.scrollLeft < body.clientWidth / 2 ? 'settings' : 'workspace';
      setActiveTab((prev) => (prev === next ? prev : next));
    };
    body.addEventListener('scroll', onScroll, { passive: true });
    return () => body.removeEventListener('scroll', onScroll);
  }, []);

  const handleTabClick = (tab: StudioTab) => {
    setActiveTab(tab);
    const body = bodyRef.current;
    if (!body || body.scrollWidth <= body.clientWidth + 1) return;
    body.scrollTo({
      left: tab === 'settings' ? 0 : body.clientWidth,
      behavior: 'smooth',
    });
  };

  // Inline example hint only surfaces while every slot is still empty —
  // once the user uploads (or a sample loads) the chips get out of the way.
  const hasAnySlot = Object.values(app.slots).some(Boolean);

  return (
    <div className="studio">
      <header className="studio-hero">
        <div className="studio-hero-main">
          <div className="studio-hero-titlebar">
            <svg
              className="studio-hero-icon"
              viewBox="0 0 32 32"
              width="40"
              height="40"
              aria-hidden
            >
              <rect x="5" y="6" width="22" height="20" rx="3" className="studio-hero-icon-card" />
              <path
                d="M16 6 L16 26"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="3 2.5"
                className="studio-hero-icon-fold"
              />
            </svg>
            <h1>Print-and-fold card PDF generator</h1>
          </div>
          <div className="studio-hero-badges" aria-label="Key features">
            <span className="hero-badge">Free</span>
            <span className="hero-badge">No account</span>
            <span className="hero-badge">No uploads — runs in your browser</span>
          </div>
          <p className="studio-hero-desc">
            A helper tool for composing a print-ready PDF of a foldable DIY
            card — greeting cards, game cards, prototyping cards, anything you
            want printed and folded in half. Follow the steps below: update
            the settings, drop in your images, tune against the live preview,
            then download the PDF when it looks right.
          </p>
        </div>
      </header>

      <nav
        className="studio-tabs"
        role="tablist"
        aria-label="Studio sections"
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'settings'}
          className={`studio-tab${activeTab === 'settings' ? ' active' : ''}`}
          onClick={() => handleTabClick('settings')}
        >
          Settings
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'workspace'}
          className={`studio-tab${activeTab === 'workspace' ? ' active' : ''}`}
          onClick={() => handleTabClick('workspace')}
        >
          Workspace
        </button>
      </nav>

      <div className="studio-body" ref={bodyRef}>
        <aside className="studio-left">
          <div className="studio-step-label">Step 1 · Settings</div>

          <div className={`card-type-card settings-group${cardTypeCollapsed ? ' collapsed' : ''}`}>
            <button
              type="button"
              className="settings-group-toggle"
              onClick={() => setCardTypeCollapsed((c) => !c)}
              aria-expanded={!cardTypeCollapsed}
            >
              <h3 className="settings-group-title">
                Card type
                <span className="card-type-summary">
                  {insideEnabled ? 'Two-sided' : 'Single-sided'}
                </span>
              </h3>
              <span className="settings-group-chev" aria-hidden>
                <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 6 8 10 12 6" />
                </svg>
              </span>
            </button>
            {!cardTypeCollapsed && (
              <div className="settings-group-body">
                <p className="settings-group-intro">How many sides do you want to print on?</p>
                <div className="card-type-toggle" role="radiogroup" aria-label="Card type">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!insideEnabled}
                    className={`card-type-opt${!insideEnabled ? ' active' : ''}`}
                    onClick={() => handleToggleInside(false)}
                  >
                    <span className="card-type-label">Single-sided</span>
                    <span className="card-type-detail">front + back</span>
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={insideEnabled}
                    className={`card-type-opt${insideEnabled ? ' active' : ''}`}
                    onClick={() => handleToggleInside(true)}
                  >
                    <span className="card-type-label">Two-sided</span>
                    <span className="card-type-detail">+ inside content + duplex</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <SettingsGroupCardPaper app={app} />
          <SettingsGroupPrint app={app} insideEnabled={insideEnabled} />
          {insideEnabled && <SettingsGroupCalibration app={app} />}
        </aside>

        <section className="studio-right">
          <article className="studio-block">
            <div className="studio-step-label">Step 2 · Drop in your images</div>
            {!hasAnySlot && <ExampleInlineHint app={app} />}
            <ImageInputs
              slots={app.slots}
              cardSize={app.cardSize}
              orientation={app.orientation}
              preserveResolution={app.preserveResolution}
              onSlotChange={app.setSlot}
              onOrientationChange={app.setOrientation}
              insideEnabled={insideEnabled}
            />
          </article>

          <article className="studio-block">
            <div className="studio-step-label">Step 3 · Preview</div>
            <SpreadPreview
              slots={app.slots}
              cardSize={app.cardSize}
              paperSize={app.paperSize}
              orientation={app.orientation}
              drift={app.drift}
              marks={app.marks}
              printer={app.printer}
              insideEnabled={insideEnabled}
            />
          </article>

          <article className="studio-block studio-cta">
            <div className="studio-step-label">Step 4 · Download</div>
            <MainDownloadButton app={app} />
          </article>
        </section>
      </div>
    </div>
  );
}
